import { Injectable, inject } from '@angular/core';

import { UserPreferencesService } from '../preferences/user-preferences.service';

/**
 * Communication Platform v2's Sound Notifications. Synthesizes a short tone via the Web
 * Audio API instead of shipping an audio asset file -- one oscillator-based "ding", played
 * per-notification-type through NotificationBellComponent (keyed by Notification.Type,
 * e.g. "ConversationMessageReceived"/"TicketAssigned"/"ConversationTransferred" -- see
 * NotifyOwnerOnInboundMessageHandler and its siblings on the backend for where those
 * strings come from), gated by UserPreferencesService's enabled/volume/per-type-mute state.
 *
 * Browser autoplay restriction: browsers block any audio -- including a synthesized tone,
 * not just <audio src> playback -- from starting before the user has interacted with the
 * page at all (Chrome's autoplay policy, Safari's equivalent). This is a real, unavoidable
 * browser policy, not a bug. This service tracks the first pointerdown/keydown on the
 * document and silently skips playback before that happens, the same honest-limitation
 * handling this codebase already uses elsewhere for browser-restricted features (e.g.
 * screen-recording's MP4-only constraint) rather than surfacing a confusing AudioContext
 * error.
 */
@Injectable({ providedIn: 'root' })
export class SoundNotificationService {
  private readonly preferences = inject(UserPreferencesService);

  private audioContext?: AudioContext;
  private hasUserGesture = false;

  constructor() {
    if (typeof document === 'undefined') {
      return;
    }

    const markGesture = () => {
      this.hasUserGesture = true;
      document.removeEventListener('pointerdown', markGesture);
      document.removeEventListener('keydown', markGesture);
    };

    document.addEventListener('pointerdown', markGesture, { once: true });
    document.addEventListener('keydown', markGesture, { once: true });
  }

  /** Plays a short tone for the given notification type, unless sound notifications are
   * disabled globally, this specific type is muted, or the browser hasn't yet seen a user
   * gesture on this page load (see this class's own remarks on why that last case is
   * silent, not an error). */
  play(type: string): void {
    if (!this.preferences.soundNotificationsEnabled() || this.preferences.mutedSoundNotificationTypes().has(type)) {
      return;
    }

    this.playTone();
  }

  /** The Sound Settings popover's "Preview" button -- always plays (ignoring the enabled/
   * muted preferences, since the point is letting the user hear the tone before deciding
   * whether to enable it) but still respects the browser-gesture restriction, which by
   * construction can never block a click on the very button that calls this. */
  playPreview(): void {
    this.playTone();
  }

  private playTone(): void {
    if (!this.hasUserGesture || typeof AudioContext === 'undefined') {
      return;
    }

    try {
      this.emitTone();
    } catch {
      // Some browsers still reject AudioContext creation/resume in edge cases despite the
      // gesture tracking above -- fail silently, a sound notification is never worth
      // surfacing an error over.
    }
  }

  private emitTone(): void {
    this.audioContext ??= new AudioContext();

    if (this.audioContext.state === 'suspended') {
      void this.audioContext.resume();
    }

    const context = this.audioContext;
    const oscillator = context.createOscillator();
    const gain = context.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.value = 880;

    const volume = this.preferences.soundNotificationsVolume();
    const now = context.currentTime;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(volume * 0.3, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);

    oscillator.connect(gain);
    gain.connect(context.destination);

    oscillator.start(now);
    oscillator.stop(now + 0.3);
  }
}
